/**
 * useOnboardingStepComplete - Marks an onboarding step locally and syncs to server.
 *
 * Consolidates the repeated pattern of:
 *   markOnboardingStep(step, ts)
 *   onboardingAPI.patchSteps({ [step]: ts }).catch(() => {})
 *
 * The server sync is fire-and-forget — it never blocks the caller's UX.
 *
 * @returns {Function} completeStep(step) — call after a user action succeeds
 * @atomic - single responsibility (<30 lines)
 */
import { useCallback } from 'react'
import { markOnboardingStep } from '../components/onboarding/OnboardingProgress'
import { onboardingAPI } from '../services/api/onboarding'
import { createLogger } from '../utils/logger'

const logger = createLogger('OnboardingStepComplete')

// Clearing this key lets hydration re-run on next page load so the server's
// canonical state (which may now include the just-completed step) is fetched.
const HYDRATION_SESSION_KEY = 'onboarding_hydrated_session'

const useOnboardingStepComplete = () => {
  const completeStep = useCallback((step) => {
    const ts = Date.now()
    markOnboardingStep(step, ts)
    // Allow hydration to re-run on the next page load so the server's view
    // stays in sync even if patchSteps fails below.
    try { sessionStorage.removeItem(HYDRATION_SESSION_KEY) } catch { /* noop */ }
    onboardingAPI.patchSteps({ [step]: ts }).catch(e =>
      logger.warn('Failed to sync onboarding step to server', e, { step })
    )
  }, [])

  return completeStep
}

export default useOnboardingStepComplete
